/**
 * Email-invitation panel: invite by email + role, and a history of every
 * invitation (pending / accepted / expired / revoked) with its email-delivery
 * status. Pending rows expose copy-link / resend / revoke. Fully callback-driven
 * — the host supplies the data and the async `onInvite` / `onResend` / `onRevoke`
 * callbacks (backed by `./teams/invitations-api`), so this imports no app router,
 * fetch client, or toast. Styled with the shipped Tangle Quiet tokens (`var(--*)`).
 *
 * Mount alongside a list-only `MembersPanel` (`showInviteForm={false}`): the
 * members panel shows accepted members, this owns the invite flow + pending list.
 */

import { useState } from 'react'
import type { WorkspaceRole } from '../../teams/roles'
import { hasWorkspaceRole } from '../../teams/roles'
import type { InvitationView, InvitationsPanelProps } from '../contracts'
import { RoleSelect } from './RoleSelect'

const BADGE_BASE = 'inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.05em]'

// Lifecycle status: green = in, amber = awaiting, red = killed, muted = lapsed.
const STATUS_BADGE: Record<InvitationView['status'], string> = {
  accepted: 'border-[var(--surface-success-border)] bg-[var(--surface-success-bg)] text-[var(--surface-success-text)]',
  pending: 'border-[var(--surface-warning-border)] bg-[var(--surface-warning-bg)] text-[var(--surface-warning-text)]',
  revoked: 'border-[var(--surface-danger-border)] bg-[var(--surface-danger-bg)] text-[var(--surface-danger-text)]',
  expired: 'border-[var(--border-default)] text-[var(--text-muted)]',
}

// Delivery status: red = bounced, amber = not yet attempted, muted = the normal happy path.
const EMAIL_BADGE: Record<InvitationView['emailStatus'], string> = {
  sent: 'border-[var(--border-default)] text-[var(--text-muted)]',
  not_sent: 'border-[var(--surface-warning-border)] bg-[var(--surface-warning-bg)] text-[var(--surface-warning-text)]',
  failed: 'border-[var(--surface-danger-border)] bg-[var(--surface-danger-bg)] text-[var(--surface-danger-text)]',
}

/** Uppercase the role's first letter in JS (not CSS `capitalize`, which would
 *  also hit the lowercase "expires" in the same line). */
function capitalize(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1)
}

export function InvitationsPanel({
  invitations,
  currentRole,
  onInvite,
  onResend,
  onRevoke,
  onCopy,
  onNotice,
}: InvitationsPanelProps) {
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>('editor')
  const [inviting, setInviting] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const canManage = hasWorkspaceRole(currentRole, 'admin')

  function notify(kind: 'success' | 'error', message: string) {
    onNotice?.({ kind, message })
  }

  async function submitInvite() {
    const email = inviteEmail.trim()
    if (!email || inviting) return
    setInviting(true)
    try {
      await onInvite({ email, role: inviteRole })
      notify('success', `Invitation sent to ${email}`)
      setInviteEmail('')
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to invite')
    } finally {
      setInviting(false)
    }
  }

  async function copyLink(inviteUrl: string) {
    try {
      if (onCopy) await onCopy({ inviteUrl })
      else await navigator.clipboard.writeText(inviteUrl)
      notify('success', 'Invite link copied')
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to copy link')
    }
  }

  async function resend(invitationId: string) {
    setBusyId(invitationId)
    try {
      await onResend({ invitationId })
      notify('success', 'Invitation resent')
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to resend invitation')
    } finally {
      setBusyId(null)
    }
  }

  async function revoke(invitationId: string) {
    setBusyId(invitationId)
    try {
      await onRevoke({ invitationId })
      notify('success', 'Invitation revoked')
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to revoke invitation')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className="flex flex-col gap-4">
      {canManage && (
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-[var(--text-muted)]">Invite by email</label>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="colleague@example.com"
              value={inviteEmail}
              aria-label="Invite email address"
              onChange={(event) => setInviteEmail(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') void submitInvite() }}
              className="flex-1 rounded border border-[var(--border-default)] bg-[var(--bg-input)] px-3 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
            />
            <RoleSelect
              value={inviteRole}
              ariaLabel="Invite role"
              onChange={setInviteRole}
            />
            <button
              type="button"
              onClick={() => void submitInvite()}
              disabled={inviting || !inviteEmail.trim()}
              className="rounded bg-[var(--brand-primary)] px-3 py-1.5 text-sm text-[hsl(var(--primary-foreground))] disabled:opacity-50"
            >
              {inviting ? 'Inviting…' : 'Invite'}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 border-t border-[var(--border-default)] pt-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-[var(--text-primary)]">Invitation history</h4>
          <span className="text-xs text-[var(--text-muted)]">{invitations.length} total</span>
        </div>
        {invitations.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">No invitations yet.</p>
        ) : (
          invitations.map((invitation) => (
            <InvitationRow
              key={invitation.id}
              invitation={invitation}
              canManage={canManage}
              busy={busyId === invitation.id}
              onCopy={() => void copyLink(invitation.inviteUrl)}
              onResend={() => void resend(invitation.id)}
              onRevoke={() => void revoke(invitation.id)}
            />
          ))
        )}
      </div>
    </section>
  )
}

interface InvitationRowProps {
  invitation: InvitationView
  canManage: boolean
  busy: boolean
  onCopy(): void
  onResend(): void
  onRevoke(): void
}

function InvitationRow({ invitation, canManage, busy, onCopy, onResend, onRevoke }: InvitationRowProps) {
  const isPending = invitation.status === 'pending'
  const emailFailed = invitation.emailStatus === 'failed'
  const expiry = new Date(invitation.expiresAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-input)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-[var(--text-primary)]">{invitation.email}</p>
          <p className="text-xs text-[var(--text-muted)]">
            {capitalize(invitation.permissions)} · expires {expiry}
          </p>
          {emailFailed && (
            <p className="mt-1 text-xs text-[var(--surface-danger-text)]">Email was not sent — copy the link to share it.</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`${BADGE_BASE} ${STATUS_BADGE[invitation.status]}`}>
            {invitation.status}
          </span>
          <span className={`${BADGE_BASE} ${EMAIL_BADGE[invitation.emailStatus]}`}>
            {invitation.emailStatus.replace('_', ' ')}
          </span>
        </div>
      </div>
      {canManage && isPending && (
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCopy}
            disabled={busy}
            className="rounded border border-[var(--border-default)] px-2 py-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            Copy link
          </button>
          <button
            type="button"
            onClick={onResend}
            disabled={busy}
            className="rounded border border-[var(--border-default)] px-2 py-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            {busy ? 'Working…' : 'Resend'}
          </button>
          <button
            type="button"
            onClick={onRevoke}
            disabled={busy}
            className="rounded px-2 py-1 text-xs text-[var(--text-danger)] hover:bg-[var(--border-default)] disabled:opacity-50"
          >
            Revoke
          </button>
        </div>
      )}
    </div>
  )
}
